import React, { useState, useEffect, lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import Catalog from "../catalog/Catalog";
import { CatalogoEspecialTemporada } from "../catalog/CatalogoEspecialTemporada";
import About from "./About";
import Contact from "./Contact";
import Workshop from "./Workshop";

const CustomCakeBuilder = lazy(() =>
  import("../cake-builder/CustomCakeBuilder").then((m) => ({ default: m.default }))
);

export type TabId = "catalogo" | "personalizar" | "workshop" | "nosotros" | "contacto";

/** Lee la URL (hash, pathname, search) para setear el tab inicial */
function getInitialTab(): TabId {
  const hash = window.location.hash.replace("#", "").toLowerCase();
  const path = window.location.pathname.replace(/^\//, "").toLowerCase();
  const searchParams = new URLSearchParams(window.location.search);
  const queryTab = searchParams.get("tab")?.toLowerCase();

  // Ignorar hashes de postres individuales — no cambian el tab activo
  if (hash.startsWith("postre-") || hash.startsWith("detail-")) return "catalogo";

  const candidate = queryTab || path || hash;

  if (candidate === "workshop" || candidate === "talleres" || candidate === "taller") return "workshop";
  if (candidate === "nosotros" || candidate === "about") return "nosotros";
  if (candidate === "contacto" || candidate === "contact") return "contacto";
  if (candidate === "temporada" || candidate === "especial") return "catalogo";
  if (
    candidate === "personalizar" ||
    candidate === "custom" ||
    candidate === "cake-builder" ||
    candidate === "custom-cakes"
  ) {
    if (queryTab === "personalizar" || path === "personalizar") return "personalizar";
    return "catalogo";
  }

  return "catalogo";
}

const TAB_BACKGROUNDS: Record<TabId, string> = {
  catalogo: "#fff7ed",
  personalizar: "#fdf2f8",
  workshop: "#fdf4ff",
  nosotros: "#fef6ee",
  contacto: "#fff1f2",
};

interface ContentHubProps {
  activeCategory: string;
  setActiveCategory: (cat: string) => void;
  isWorkshopVisible: boolean;
  isWorkshopLoading: boolean;
}

const ContentHub: React.FC<ContentHubProps> = ({
  activeCategory,
  setActiveCategory,
  isWorkshopVisible,
  isWorkshopLoading,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>(getInitialTab);
  const [contentKey, setContentKey] = useState(0);

  /* Sincronizar hash o popstate con el tab activo */
  useEffect(() => {
    const handleUrlChange = () => {
      const newHash = window.location.hash.replace("#", "").toLowerCase();

      // NO re-renderizar si el hash es de un postre individual — el modal lo maneja
      if (newHash.startsWith("postre-") || newHash.startsWith("detail-")) return;

      const detectedTab = getInitialTab();
      setActiveTab((prev) => {
        if (prev !== detectedTab) {
          setContentKey((k) => k + 1);
        }
        return detectedTab;
      });

      // Si el hash era temporada, scrollear suavemente a la campaña
      if (window.location.hash === "#temporada" || window.location.hash === "#catalogo-temporada") {
        setTimeout(() => {
          document.getElementById("special-campaign-catalog")?.scrollIntoView({ behavior: "smooth" });
        }, 150);
      }

      // Si el hash es personalizar o custom-cakes, scrollear suavemente a la sección de tortas personalizadas
      if (window.location.hash === "#personalizar" || window.location.hash === "#custom-cakes") {
        setTimeout(() => {
          const target = document.getElementById("custom-cakes");
          if (target) {
            const yOffset = -70;
            const y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
          }
        }, 150);
      }
    };

    window.addEventListener("hashchange", handleUrlChange);
    window.addEventListener("popstate", handleUrlChange);
    return () => {
      window.removeEventListener("hashchange", handleUrlChange);
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, []);

  // Scroll inicial si se abre la página directamente con #personalizar o #custom-cakes
  useEffect(() => {
    const hash = window.location.hash.toLowerCase();
    if (hash === "#personalizar" || hash === "#custom-cakes") {
      const timer = setTimeout(() => {
        const target = document.getElementById("custom-cakes");
        if (target) {
          const yOffset = -70;
          const y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (isWorkshopLoading || isWorkshopVisible || activeTab !== "workshop") return;

    setActiveTab("catalogo");
    const path = window.location.pathname.replace(/^\//, "").toLowerCase();
    const queryTab = new URLSearchParams(window.location.search).get("tab")?.toLowerCase();
    const hasWorkshopRoute =
      ["workshop", "talleres", "taller"].includes(path) ||
      ["workshop", "talleres", "taller"].includes(queryTab || "");

    window.history.replaceState(null, "", hasWorkshopRoute ? "/#catalogo" : "#catalogo");
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  }, [activeTab, isWorkshopLoading, isWorkshopVisible]);

  return (
    <section
      id="hub"
      className="min-h-screen relative transition-colors duration-500"
      style={{ background: TAB_BACKGROUNDS[activeTab] || "#ffffff" }}
    >
      {/* ── Contenido Dinámico de la Sección Activa (Controlada desde el Navbar) ── */}
      <div
        key={contentKey}
        role="tabpanel"
        id={`panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        className="tab-content-enter"
      >
        {activeTab === "catalogo" && (
          <>
            {/* 1. Catálogo Especial de Temporada (arriba del catálogo normal) */}
            <CatalogoEspecialTemporada />

            {/* 2. Catálogo General */}
            <Catalog
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
            />

            {/* 3. Tortas Personalizadas (Crea tu Torta Perfecta + Galería de Creaciones) */}
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center py-20 space-y-3">
                  <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
                  <p className="font-body text-xs text-rose-700 font-medium">Cargando tortas personalizadas...</p>
                </div>
              }
            >
              <CustomCakeBuilder />
            </Suspense>
          </>
        )}

        {activeTab === "personalizar" && (
          <Suspense
            fallback={
              <div className="flex flex-col items-center justify-center py-32 space-y-3">
                <Loader2 className="w-9 h-9 text-rose-600 animate-spin" />
                <p className="font-body text-xs text-rose-700 font-medium">Cargando personalizador de tortas...</p>
              </div>
            }
          >
            <CustomCakeBuilder />
          </Suspense>
        )}

        {activeTab === "workshop" && isWorkshopVisible && <Workshop />}

        {activeTab === "nosotros" && <About />}

        {activeTab === "contacto" && <Contact />}
      </div>
    </section>
  );
};

export default ContentHub;
